// Helper to get info on the GPU used on the user's system.
import isEmpty from 'lodash/isEmpty.js';
import omit from 'lodash/omit.js';
import _d from '@repperjs/debug';

/* eslint-disable no-unused-vars */
const log = _d('repper:utils:environment:gpuInfo'),
    error = _d('repper:error'),
    warn = _d('repper:warning');
/* eslint-enable no-unused-vars */

type GPUInfoError = { error: string };
type GPUInfoSuccess = { gpu: string | null; gpuVendor: string | null; supportedExtensions?: string[] | null };
type GPUInfo = GPUInfoError | GPUInfoSuccess;

let gpuInfo: Partial<GPUInfo> = {};

export const isGPUInfoError = (gpuInfo: GPUInfo): gpuInfo is GPUInfoError =>
    (gpuInfo as GPUInfoError).error !== undefined;

export function getGPUInfo(withExtensions = true): GPUInfo {
    if (!isEmpty(gpuInfo)) return withExtensions ? gpuInfo : omit(gpuInfo, ['supportedExtensions']);

    let gl;

    try {
        const canvas = document.createElement('canvas');
        gl = canvas.getContext('webgl');
        if (!gl) {
            return { error: 'WebGL not supported' };
        }
        const extension = gl.getExtension('WEBGL_debug_renderer_info');
        if (extension !== null) {
            gpuInfo = {
                gpu: gl.getParameter(extension.UNMASKED_RENDERER_WEBGL),
                gpuVendor: gl.getParameter(extension.UNMASKED_VENDOR_WEBGL),
            };
        } else {
            gpuInfo = { gpu: null, gpuVendor: null };
        }
    } catch (e) {
        error('Error getting GPU info', e);
        return { error: `Error getting GPU info: ${e}` };
    }

    try {
        // get supported (and enabled?) extensions
        gpuInfo = { ...gpuInfo, supportedExtensions: gl.getSupportedExtensions() };
    } catch (e) {
        error('Error trying to get list of GPU supported extensions', e);
        return { error: `Error trying to get list of GPU supported extensions: ${e}` };
    }

    return withExtensions ? gpuInfo : omit(gpuInfo, ['supportedExtensions']);
}
